import { createServerFn } from "@tanstack/react-start";
import { cachedPlacesFetch, PLACES_TTL } from "./places-http-cache.server";
import { z } from "zod";

/**
 * Modo Inspiração — "não sei para onde ir de férias".
 *
 * Consome 1 crédito (decisão do utilizador) e devolve 4-6 destinos REAIS,
 * validados por geocodificação, com foto do Google Places. Em qualquer falha
 * depois do débito, a quota é devolvida (`releaseQuotaInternal`) — nenhuma
 * sugestão paga pode acabar em erro silencioso.
 */

export type InspirationVibe = "nature" | "beach" | "city" | "adventure" | "relax";
export type InspirationBudget = "low" | "medium" | "high";

export type InspirationDestination = {
  name: string;
  country: string;
  /** Resumo curto (2-3 frases) no idioma do utilizador. */
  summary: string;
  /** Melhor época do ano (texto curto, idioma do utilizador). */
  bestSeason: string;
  /** Dias ideais de visita. */
  idealDays: number;
  budget: InspirationBudget;
  photoUrl: string | null;
  lat: number | null;
  lng: number | null;
};

export type InspirationQuotaError = {
  reason: "anon_limit" | "free_limit" | "premium_limit" | "premium_monthly_limit";
  tier: "anon" | "free" | "premium";
  resetInMinutes: number;
};

export type InspirationResult =
  | { destinations: InspirationDestination[]; error: null }
  | {
      destinations: [];
      error: "quota_exceeded" | "ai_unavailable" | "technical_failure";
      quota?: InspirationQuotaError;
    };

const Input = z.object({
  days: z.number().int().min(1).max(30),
  vibe: z
    .array(z.enum(["nature", "beach", "city", "adventure", "relax"]))
    .max(5)
    .default([]),
  budget: z.enum(["low", "medium", "high"]).nullable(),
  language: z.string().min(2).max(8).default("pt"),
  anonId: z.string().max(120).optional(),
});

const VIBE_LABEL: Record<InspirationVibe, string> = {
  nature: "nature and landscapes",
  beach: "beach and coast",
  city: "city and culture",
  adventure: "adventure and outdoors",
  relax: "relaxation and slow pace",
};

const BUDGET_LABEL: Record<InspirationBudget, string> = {
  low: "tight budget (affordable destinations)",
  medium: "medium budget",
  high: "comfortable/high budget",
};

type AISuggestion = {
  name: string;
  country: string;
  summary: string;
  bestSeason: string;
  idealDays: number;
  budget: InspirationBudget;
};

async function askAiForDestinations(data: z.infer<typeof Input>): Promise<AISuggestion[] | null> {
  const apiKey = process.env.LOVABLE_API_KEY;
  if (!apiKey) return null;
  const system = `You are a travel inspiration curator. Suggest 6 REAL, beautiful destinations (cities, towns, villages or small regions) worldwide that most tourists overlook but that offer a lot to see and do. Prefer hidden gems over famous capitals. Spread across different continents. Match the traveler's constraints. Reply ONLY via the tool call, in language code "${data.language}".`;
  const user = `Traveler has ${data.days} days.${data.vibe.length ? ` Travel styles wanted (combine them): ${data.vibe.map((v) => VIBE_LABEL[v]).join(", ")}.` : ""}${
    data.budget ? ` Budget: ${BUDGET_LABEL[data.budget]}.` : ""
  }
For each destination return:
- name: the locality name as commonly written in ${data.language}.
- country: country name in ${data.language}.
- summary: 2 short sentences on why it is special and still under the radar.
- bestSeason: best time of year to visit (max 8 words).
- idealDays: realistic ideal trip length in days (integer, may differ from the traveler's days).
- budget: low|medium|high — typical on-site cost level.`;

  const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
    method: "POST",
    headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
    signal: AbortSignal.timeout(45_000),
    body: JSON.stringify({
      model: "google/gemini-3.7-flash",
      messages: [
        { role: "system", content: system },
        { role: "user", content: user },
      ],
      tools: [
        {
          type: "function",
          function: {
            name: "return_destinations",
            parameters: {
              type: "object",
              properties: {
                destinations: {
                  type: "array",
                  minItems: 4,
                  maxItems: 6,
                  items: {
                    type: "object",
                    properties: {
                      name: { type: "string" },
                      country: { type: "string" },
                      summary: { type: "string" },
                      bestSeason: { type: "string" },
                      idealDays: { type: "integer", minimum: 1, maximum: 30 },
                      budget: { type: "string", enum: ["low", "medium", "high"] },
                    },
                    required: ["name", "country", "summary", "bestSeason", "idealDays", "budget"],
                    additionalProperties: false,
                  },
                },
              },
              required: ["destinations"],
              additionalProperties: false,
            },
          },
        },
      ],
      tool_choice: { type: "function", function: { name: "return_destinations" } },
    }),
  });
  if (!res.ok) {
    console.error("Inspiration AI error", res.status, await res.text().catch(() => ""));
    return null;
  }
  const json = (await res.json()) as {
    choices?: Array<{ message?: { tool_calls?: Array<{ function?: { arguments?: string } }> } }>;
  };
  const args = json.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments;
  if (!args) return null;
  try {
    const parsed = JSON.parse(args) as { destinations?: AISuggestion[] };
    const list = parsed.destinations ?? [];
    return list.filter((d) => d?.name && d?.country && d?.summary);
  } catch {
    return null;
  }
}

/** Geocodificação gratuita (Nominatim) — confirma que o destino existe. */
async function geocodeDestination(
  name: string,
  country: string,
  language: string,
): Promise<{ lat: number; lng: number } | null> {
  try {
    const url = new URL("https://nominatim.openstreetmap.org/search");
    url.searchParams.set("format", "jsonv2");
    url.searchParams.set("limit", "1");
    url.searchParams.set("q", `${name}, ${country}`);
    url.searchParams.set("accept-language", language);
    const response = await fetch(url, {
      headers: { "User-Agent": "Routify/1.0 (support@routify-tour.com)", Accept: "application/json" },
      signal: AbortSignal.timeout(4500),
    });
    if (!response.ok) return null;
    const payload = (await response.json()) as Array<{ lat?: string; lon?: string }>;
    const first = payload[0];
    const lat = Number.parseFloat(first?.lat ?? "");
    const lng = Number.parseFloat(first?.lon ?? "");
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  } catch {
    return null;
  }
}

/** Foto real do destino via Google Places (text search + media). */
async function destinationPhoto(name: string, country: string): Promise<string | null> {
  const apiKey = process.env.GOOGLE_MAPS_SERVER_API_KEY || process.env.GOOGLE_MAPS_API_KEY || "";
  if (!apiKey) return null;
  const { checkGoogleApiRateLimit } = await import("./quota.server");
  if (!(await checkGoogleApiRateLimit("placeRecommendation")).ok) return null;
  try {
    const res = await cachedPlacesFetch(
      "https://places.googleapis.com/v1/places:searchText",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask": "places.id,places.photos",
        },
        body: JSON.stringify({ textQuery: `${name}, ${country}`, maxResultCount: 1 }),
        signal: AbortSignal.timeout(6000),
      },
      { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "inspiration_photo", sku: "places_text_search" },
    );
    if (!res.ok) return null;
    const j = (await res.json()) as { places?: Array<{ photos?: Array<{ name?: string }> }> };
    const photoName = j.places?.[0]?.photos?.[0]?.name;
    if (!photoName) return null;
    const ph = await cachedPlacesFetch(
      `https://places.googleapis.com/v1/${photoName}/media?skipHttpRedirect=true&maxWidthPx=800`,
      { method: "GET", headers: { "X-Goog-Api-Key": apiKey }, signal: AbortSignal.timeout(6000) },
      { kind: "photo", ttlMs: 0, origin: "inspiration_photo", sku: "place_photo" },
    );
    if (!ph.ok) return null;
    const pj = (await ph.json()) as { photoUri?: string };
    return pj.photoUri ?? null;
  } catch (e) {
    console.warn("Inspiration photo lookup failed", e);
    return null;
  }
}

export const suggestDestinations = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Input.parse(d))
  .handler(async ({ data }): Promise<InspirationResult> => {
    const { consumeQuotaInternal, releaseQuotaInternal } = await import("./quota.server");
    const quota = await consumeQuotaInternal(data.anonId);
    if (!quota.ok) {
      return {
        destinations: [],
        error: "quota_exceeded",
        quota: { reason: quota.reason, tier: quota.tier, resetInMinutes: quota.resetInMinutes },
      };
    }

    try {
      const suggestions = await askAiForDestinations(data);
      if (!suggestions || suggestions.length === 0) {
        await releaseQuotaInternal(quota);
        return { destinations: [], error: "ai_unavailable" };
      }

      // Validação real em paralelo: geocodificação gratuita manda; destinos
      // que a IA inventou e o mundo não conhece são descartados.
      const validated = await Promise.all(
        suggestions.slice(0, 6).map(async (s): Promise<InspirationDestination | null> => {
          const geo = await geocodeDestination(s.name, s.country, data.language);
          if (!geo) return null;
          const photoUrl = await destinationPhoto(s.name, s.country);
          return {
            name: s.name,
            country: s.country,
            summary: s.summary.slice(0, 400),
            bestSeason: s.bestSeason.slice(0, 120),
            idealDays: Math.max(1, Math.min(30, Math.trunc(s.idealDays || data.days))),
            budget: s.budget,
            photoUrl,
            lat: geo.lat,
            lng: geo.lng,
          };
        }),
      );

      const destinations = validated.filter((d): d is InspirationDestination => d !== null);
      if (destinations.length === 0) {
        await releaseQuotaInternal(quota);
        return { destinations: [], error: "technical_failure" };
      }
      return { destinations, error: null };
    } catch (e) {
      console.error("suggestDestinations failed", e);
      await releaseQuotaInternal(quota);
      return { destinations: [], error: "technical_failure" };
    }
  });
